/**
 * Firebase Auth Integration Component
 * This component provides a toggle to switch between Firebase and Backend authentication
 * while maintaining all existing functionality
 */

import { useState } from "react";
import { toast } from "sonner";
import { useFirebaseAuth } from "~/hook/useFirebaseAuth";
import useLocale from "~/hook/useLocale";
import { setLocalStorage } from "~/lib/helperFuncation/localStorageService";
import {
  setFirebasePhoneNumber,
  setFirebaseToken,
  setFirebaseUid,
  setIsDialogClose,
  setIsUserLoginTrue,
  setLoading,
  setLoginStep,
  setMessage,
  setUseFirebaseAuth,
} from "~/lib/store/slice/loginSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { AUTH_COOKIE_TOKEN, USER_COUNTRY_PHONE_CODE, USER_MOBILE_NUMBER } from "~/utils/constant";
import { setCookie } from "~/utils/cookies";
import { getExpiryFromToken } from "~/utils/jwt";

interface FirebaseAuthWrapperProps {
  phoneNumber: string;
  countryCode: number;
  recaptchaToken: string | null;
  onFirebaseSuccess?: (token: string) => void;
  onFirebaseError?: (error: string) => void;
}

/**
 * Hook to handle Firebase OTP sending
 * Now uses existing reCAPTCHA v2 token instead of invisible reCAPTCHA
 */
export const useFirebaseOTPSender = ({ phoneNumber, countryCode, recaptchaToken, onFirebaseSuccess, onFirebaseError }: FirebaseAuthWrapperProps) => {
  const dispatch = useAppDispatch();
  const { sendFirebaseOTP, resendFirebaseOTP, firebaseLoading, firebaseError, isFirebaseRecaptchaReady, verifyFirebaseOTP } = useFirebaseAuth();
  const [otpSent, setOtpSent] = useState(false);

  // Handle send OTP with optional token parameter (for on-submit token generation)
  const handleSendFirebaseOTPWithToken = async (_tokenToUse?: string): Promise<boolean> => {
    if (!phoneNumber) {
      const error = "Phone number is required";
      onFirebaseError?.(error);
      return false;
    }

    // Set Redux loading state to show spinner
    dispatch(setLoading(true));

    // Format phone number with country code for Firebase
    const fullPhoneNumber = `+${countryCode}${phoneNumber}`;

    try {
      const success = await sendFirebaseOTP(fullPhoneNumber, "");

      // Clear Redux loading state
      dispatch(setLoading(false));

      if (success) {
        setOtpSent(true);

        // Store phone number and country code in localStorage (same as backend OTP)
        setLocalStorage(USER_MOBILE_NUMBER, phoneNumber);
        setLocalStorage(USER_COUNTRY_PHONE_CODE, countryCode.toString());

        // Store phone number in Redux for display
        dispatch(setFirebasePhoneNumber(fullPhoneNumber));

        // Create masked phone message
        const maskedPhone = phoneNumber.slice(-4);
        const maskedMessage = `An OTP has been sent to your registered mobile number ending in ******${maskedPhone}. Please enter it below.`;

        dispatch(setMessage(maskedMessage));

        dispatch(setLoginStep(2));
        onFirebaseSuccess?.("OTP sent successfully");
        return true;
      } else {
        dispatch(setLoading(false));
        const errorMsg = firebaseError || "Failed to send OTP";
        onFirebaseError?.(errorMsg);
        return false;
      }
    } catch (error) {
      dispatch(setLoading(false));
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      onFirebaseError?.(errorMsg);
      return false;
    }
  };

  const handleSendFirebaseOTP = async (): Promise<boolean> => {
    if (!phoneNumber) {
      const error = "Phone number is required";
      onFirebaseError?.(error);
      return false;
    }

    dispatch(setLoading(true));
    const fullPhoneNumber = `+${countryCode}${phoneNumber}`;

    try {
      const success = await sendFirebaseOTP(fullPhoneNumber, "");

      // Clear Redux loading state
      dispatch(setLoading(false));

      if (success) {
        setOtpSent(true);

        // Store phone number and country code in localStorage (same as backend OTP)
        setLocalStorage(USER_MOBILE_NUMBER, phoneNumber);
        setLocalStorage(USER_COUNTRY_PHONE_CODE, countryCode.toString());

        // Store phone number in Redux for display
        dispatch(setFirebasePhoneNumber(fullPhoneNumber));

        // Create masked phone message: "An OTP has been sent to your registered mobile number ending in ******3647"
        const maskedPhone = phoneNumber.slice(-4); // Last 4 digits
        const maskedMessage = `An OTP has been sent to your registered mobile number ending in ******${maskedPhone}. Please enter it below.`;

        // Store message (shows in the info box on VerifyOtpComponent)
        dispatch(setMessage(maskedMessage));

        dispatch(setLoginStep(2)); // Move to OTP verification step
        onFirebaseSuccess?.("OTP sent successfully");
        return true;
      } else {
        dispatch(setLoading(false));
        const errorMsg = firebaseError || "Failed to send OTP ";
        onFirebaseError?.(errorMsg);
        return false;
      }
    } catch (error) {
      dispatch(setLoading(false));
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      onFirebaseError?.(errorMsg);
      return false;
    }
  };

  const handleResendFirebaseOTP = async (): Promise<boolean> => {
    if (!phoneNumber) {
      const error = "Phone number is required";
      onFirebaseError?.(error);
      return false;
    }

    dispatch(setLoading(true));
    const fullPhoneNumber = `+${countryCode}${phoneNumber}`;

    try {
      const success = await resendFirebaseOTP(fullPhoneNumber, "");

      // Clear Redux loading state
      dispatch(setLoading(false));

      if (success) {
        // Create masked phone message: "An OTP has been resent to your registered mobile number..."
        const maskedPhone = phoneNumber.slice(-4); // Last 4 digits
        const maskedMessage = `An OTP has been resent to your registered mobile number ending in ******${maskedPhone}. Please enter it below.`;

        // Update message
        dispatch(setMessage(maskedMessage));

        onFirebaseSuccess?.("OTP resent successfully");
        return true;
      } else {
        dispatch(setLoading(false));
        const errorMsg = firebaseError || "Failed to resend OTP";
        onFirebaseError?.(errorMsg);
        return false;
      }
    } catch (error) {
      dispatch(setLoading(false));
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      onFirebaseError?.(errorMsg);
      return false;
    }
  };

  return {
    sendOTP: handleSendFirebaseOTP,
    sendOTPWithToken: handleSendFirebaseOTPWithToken, // Accept token parameter for on-submit generation
    resendOTP: handleResendFirebaseOTP,
    isLoading: firebaseLoading,
    error: firebaseError,
    otpSent,
    isReady: isFirebaseRecaptchaReady,
  };
};

/**
 * Component to handle Firebase OTP verification
 */
export const useFirebaseOTPVerifier = () => {
  const dispatch = useAppDispatch();
  const { verifyFirebaseOTP, firebaseLoading, firebaseError, getFirebaseToken } = useFirebaseAuth();
  const { navigatTo } = useAppSelector((state) => state.login);
  const { navigate } = useLocale();

  const handleVerifyFirebaseOTP = async (otp: string) => {
    // Set Redux loading state to show spinner
    dispatch(setLoading(true));

    const firebaseToken = await verifyFirebaseOTP(otp);

    // Clear Redux loading state
    dispatch(setLoading(false));

    if (firebaseToken) {
      // Store Firebase token in Redux
      dispatch(setFirebaseToken(firebaseToken));

      // Extract user info from Firebase token if needed
      try {
        const tokenParts = firebaseToken.split(".");
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          dispatch(setFirebaseUid(payload.user_id || payload.sub));
        }
      } catch (err) {
        console.error("Error parsing Firebase token:", err);
      }

      // // Set authentication cookie
      // const expiry = getExpiryFromToken(firebaseToken);
      // const maxAge = expiry && expiry > Date.now() ? Math.floor((expiry - Date.now()) / 1000) : 60 * 60 * 24;

      // setCookie(AUTH_COOKIE_TOKEN, firebaseToken, {
      //   path: "/",
      //   expires: maxAge,
      //   secure: false,
      //   sameSite: "lax",
      // });

      // Mark user as logged in
      dispatch(setIsUserLoginTrue());
      dispatch(setIsDialogClose());

      toast.success("Login successful with Firebase");

      // Navigate if needed
      if (navigatTo) {
        navigate(navigatTo);
      }

      return firebaseToken;
    }

    return null;
  };

  return {
    verifyOTP: handleVerifyFirebaseOTP,
    isLoading: firebaseLoading,
    error: firebaseError,
    getToken: getFirebaseToken,
  };
};
